import React from 'react'
import clsx from 'clsx'
import { ClipboardTick, Messages2, Message } from 'iconsax-react'
import { RiCheckboxBlankLine, RiCheckboxLine } from 'react-icons/ri'
import { useTheme } from '@mui/material'
import { useHistory } from 'react-router'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import Button from '../../../components/atoms/Button2'

import { Routes } from '../../../Routes'
import { formatDateRelative, replaceMentions } from '../../../utilities'
import { markRead as markNotificationRead } from '../../../slices/notification'
import { selectDMs } from '../../../slices/dms'
import { useAgent } from '../../../providers/AgentProvider'
import { useUserAPI } from '../../../providers/UserAPIProvider'
import {
  useSocket,
  CONVERSATION_FILTERS
} from '../../../providers/SocketProvider'
import useStyles from './styles'

type NotificationProps = {
  notification: APINotification
  checked: boolean
  toggleCheck: () => void
  isLast: boolean
  onMarkRead: () => void
}

const Notification: React.FC<NotificationProps> = ({
  notification,
  checked,
  toggleCheck,
  isLast,
  onMarkRead
}) => {
  const theme = useTheme()
  const history = useHistory()
  const dispatch = useAppDispatch()

  const { dms } = useAppSelector(selectDMs)

  const { currentUser } = useAgent()
  const { allUsers, getUsers } = useUserAPI()
  const {
    api: { handleFilterChange }
  } = useSocket()

  const [hovering, setHovering] = React.useState(false)

  const isUnread = React.useMemo(
    () => notification.read === null,
    [notification]
  )
  const isPeerChat = React.useMemo(
    () => Object.keys(dms).includes(notification.source_session_id),
    [dms, notification.source_session_id]
  )

  const CheckIcon = React.useMemo(
    () => (checked ? RiCheckboxLine : RiCheckboxBlankLine),
    [checked]
  )

  const Icon = React.useMemo(() => {
    if (notification.source === 'task') {
      return ClipboardTick
    }

    return isPeerChat ? Messages2 : Message
  }, [notification.source, isPeerChat])

  const agents = React.useMemo(
    () => [...Object.values(getUsers(false)), currentUser()].filter(Boolean),
    [getUsers, currentUser]
  )

  const user = React.useMemo(
    () => allUsers[notification.src_user_id],
    [allUsers, notification.src_user_id]
  )

  const body = React.useMemo(
    () => replaceMentions(notification.body, agents),
    [notification.body, agents]
  )

  const title = React.useMemo(() => {
    const rawTitle = (() => {
      if (notification.source === 'task') {
        return notification.title ?? notification.body ?? 'Notification'
      }

      if (!user?.name) {
        return 'Notification'
      }

      return `${user.name} mentioned you`
    })()

    return replaceMentions(rawTitle, agents)
  }, [user, notification, agents])

  const time = React.useMemo(
    () => formatDateRelative(notification.stamp, true),
    [notification.stamp]
  )

  const markRead = React.useCallback(() => {
    dispatch(markNotificationRead(notification.id))
    onMarkRead()
  }, [dispatch, notification.id, onMarkRead])

  const onRowClick = React.useCallback(() => {
    markRead()

    const { source_id, source_session_id } = notification

    // Task case
    if (notification.source === 'task') {
      history.push(`${Routes.TASKS}/${source_id}`)
      return
    }

    // Message case
    if (notification.type === 'mention' && source_session_id) {
      // Peer chat
      if (isPeerChat) {
        history.push(`${Routes.PEER_CHAT}?session=${source_session_id}`)
        return
      }

      // Normal conversations
      handleFilterChange(CONVERSATION_FILTERS.OPEN, false)
      history.push(
        `${Routes.CLIENT_CHATS}?session=${source_session_id}&id=${source_id}`
      )
    }
  }, [markRead, notification, isPeerChat, history, handleFilterChange])

  const showBody = React.useMemo(() => {
    return title !== body
  }, [title, body])

  const classes = useStyles()

  return (
    <div
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onClick={onRowClick}
      className={clsx(classes.root, !isLast && classes.rootBorder)}
    >
      <div className={classes.checkboxArea}>
        {(hovering || checked) && isUnread && (
          <CheckIcon
            size={theme.size.base}
            onClick={e => {
              e.stopPropagation()
              toggleCheck()
            }}
          />
        )}
      </div>

      <div className={classes.icon}>
        <Icon
          size={theme.size.xl}
          variant='Bulk'
          color={isUnread ? theme.palette.primary.main : '#5E5E5E'}
        />
      </div>

      <div className={clsx(classes.bodyArea, isUnread && classes.bodyNew)}>
        <div className={classes.title}>{title}</div>

        {showBody && <div className={classes.body}>{body}</div>}
      </div>

      <div className={classes.rightArea}>
        {hovering ? (
          !isUnread ? (
            <div className={classes.time}>{time}</div>
          ) : (
            <div className={classes.markRead}>
              <Button
                onClick={e => {
                  e.stopPropagation()
                  markRead()
                }}
                className={classes.markReadButton}
              >
                Mark read
              </Button>
            </div>
          )
        ) : (
          <div className={classes.time}>{time}</div>
        )}
      </div>
    </div>
  )
}

export default Notification
